import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, onSelect, interactive = false, size = 18 }) {
  return (
    <div className="star-rating" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={`star ${i <= rating ? 'filled' : ''}`}
          fill={i <= rating ? '#f59e0b' : 'none'}
          stroke={i <= rating ? '#f59e0b' : '#6b7280'}
          style={interactive ? { cursor: 'pointer' } : {}}
          onClick={interactive ? () => onSelect(i) : undefined}
        />
      ))}
    </div>
  );
}

export default function ProductReviews({ productSlug, productName }) {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, rating_count: 0, distribution: {} });
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!productSlug) return;
    fetch(`${API}/api/reviews/product/${productSlug}`)
      .then(r => r.json())
      .then(d => {
        setReviews(d.reviews || []);
        setStats({ average_rating: d.average_rating, rating_count: d.rating_count, distribution: d.distribution || {} });
      })
      .catch(() => {});
  }, [productSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { setError('Bitte wählen Sie eine Sternebewertung'); return; }
    if (text.length < 10) { setError('Bitte schreiben Sie mindestens 10 Zeichen'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/reviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_slug: productSlug, rating, title, text }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setShowForm(false);
        setRating(0); setTitle(''); setText('');
      } else {
        setError(data.detail || 'Fehler beim Senden');
      }
    } catch {
      setError('Verbindungsfehler');
    }
    setSubmitting(false);
  };

  const handleHelpful = async (review) => {
    try {
      await fetch(`${API}/api/reviews/helpful/${productSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ created_at: review.created_at }),
      });
      setReviews(prev => prev.map(r => r.created_at === review.created_at ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r));
    } catch {}
  };

  const maxDist = Math.max(...Object.values(stats.distribution || {}).map(Number), 1);

  return (
    <div className="reviews-section" data-testid="reviews-section">
      <h2 className="reviews-title">Kundenbewertungen</h2>

      {/* Summary */}
      <div className="reviews-summary" data-testid="reviews-summary">
        <div className="reviews-avg">
          <span className="avg-number">{stats.average_rating || '—'}</span>
          <StarRating rating={Math.round(stats.average_rating)} size={20} />
          <span className="review-count">{stats.rating_count} {stats.rating_count === 1 ? 'Bewertung' : 'Bewertungen'}</span>
        </div>
        <div className="reviews-bars">
          {[5, 4, 3, 2, 1].map(n => (
            <div className="rating-bar-row" key={n}>
              <span className="bar-label">{n}<Star size={12} fill="#f59e0b" stroke="#f59e0b" /></span>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${((stats.distribution?.[String(n)] || 0) / maxDist) * 100}%` }} />
              </div>
              <span className="bar-count">{stats.distribution?.[String(n)] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Write Review Button */}
      {!showForm && !submitted && (
        <button className="btn-write-review" data-testid="write-review-btn" onClick={() => {
          if (!token) { setError('Bitte melden Sie sich an, um eine Bewertung zu schreiben'); return; }
          setShowForm(true);
        }}>
          <Star size={16} /> Bewertung schreiben
        </button>
      )}

      {submitted && <div className="review-success" data-testid="review-submitted">Vielen Dank! Ihre Bewertung wird nach Prüfung veröffentlicht.</div>}

      {error && <div className="review-error">{error}</div>}

      {/* Review Form */}
      {showForm && (
        <form className="review-form" data-testid="review-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Ihre Bewertung *</label>
            <StarRating rating={rating} onSelect={setRating} interactive size={28} />
          </div>
          <div className="form-group">
            <label>Titel</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Kurze Zusammenfassung" data-testid="review-title-input" />
          </div>
          <div className="form-group">
            <label>Bewertungstext *</label>
            <textarea rows={4} value={text} onChange={e => setText(e.target.value)} placeholder="Was hat Ihnen gefallen? Was kann verbessert werden?" data-testid="review-text-input" />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-submit-review" disabled={submitting} data-testid="submit-review-btn">
              <Send size={14} /> {submitting ? 'Senden...' : 'Bewertung absenden'}
            </button>
            <button type="button" className="btn-cancel-review" onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {/* Review List */}
      <div className="reviews-list" data-testid="reviews-list">
        {reviews.length === 0 && !showForm && (
          <p className="no-reviews">Noch keine Bewertungen. Seien Sie der Erste!</p>
        )}
        {reviews.map((r, i) => (
          <div className="review-card" key={i} data-testid={`review-card-${i}`}>
            <div className="review-header">
              <StarRating rating={r.rating} size={14} />
              {r.title && <strong className="review-title-text">{r.title}</strong>}
            </div>
            <p className="review-text">{r.text}</p>
            <div className="review-footer">
              <span className="review-author">{r.author_name || 'Anonym'}</span>
              <span className="review-date">{r.created_at ? new Date(r.created_at).toLocaleDateString('de-DE') : ''}</span>
              <button className="btn-helpful" onClick={() => handleHelpful(r)}>
                <ThumbsUp size={12} /> Hilfreich ({r.helpful_count || 0})
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
